/**
 * CaseReadinessPanel - Document Readiness Status Display
 * ========================================================
 * 
 * Shows whether a case is ready for accountant review with clear blockers.
 * Used in wizard sidebar and case detail pages.
 * 
 * P0: Document Readiness System - Frontend Component
 * @author ClaimSync Team
 * @created August 2025
 */

import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  FileText,
  Building2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Upload,
  FileSpreadsheet
} from 'lucide-react';

interface ReadinessCheck {
  name: string;
  status: 'passed' | 'failed' | 'pending' | 'not_required';
  required: boolean;
  message: string;
  details?: Record<string, any>;
}

interface ReadinessResult {
  case_id: string;
  is_ready: boolean;
  status: 'ready' | 'not_ready' | 'blocked';
  overall_message: string;
  checks: ReadinessCheck[];
  blocking_issues: string[];
  warnings: string[];
  entity_type: string | null;
  assessed_at: string;
}

interface CaseReadinessPanelProps {
  caseId: string;
  /** Compact mode for sidebar display */
  compact?: boolean;
  /** Callback when readiness status changes */
  onStatusChange?: (isReady: boolean) => void;
  /** Show refresh button */
  showRefresh?: boolean;
  /** Custom class name */
  className?: string;
}

// Hue law (K3 review, 2026-08-23): adverse/blocking/failure is crimson's
// exclusive channel; passed states read HUELESS (verified grey, never green,
// never orange — the eye should slide past them to the crimson). No orange-alpha
// washes (that renders brown over near-black). `color` = icon hue, `text` =
// status-message hue, `accent` = the single 2px crimson left-bar for the adverse
// state (failed only), `bg`/`border` = the (neutral) row surface.
const STATUS_CONFIG: Record<
  string,
  { icon: typeof CheckCircle; color: string; text: string; bg: string; border: string; accent?: string }
> = {
  passed: {
    icon: CheckCircle,
    color: 'var(--color-verified)',
    text: 'var(--color-text-muted)',
    bg: 'transparent',
    border: 'var(--color-border)'
  },
  failed: {
    icon: XCircle,
    color: 'var(--color-blocker)',
    text: 'var(--color-blocker)',
    bg: 'var(--color-bg-elevated)',
    border: 'var(--color-border)',
    accent: 'var(--color-blocker)'
  },
  pending: {
    icon: Clock,
    color: 'var(--color-text-muted)',
    text: 'var(--color-text-muted)',
    bg: 'transparent',
    border: 'var(--color-border)'
  },
  not_required: {
    icon: CheckCircle,
    color: 'var(--color-text-dim)',
    text: 'var(--color-text-dim)',
    bg: 'var(--color-bg-elevated)',
    border: 'var(--color-border)'
  }
};

// Business-entity acronyms that must stay all-caps (not CSS `capitalize`,
// which turns "llc" into "Llc") — the rest of the label title-cases normally.
const ENTITY_TYPE_ACRONYMS = new Set(['llc', 'llp', 'lp', 'lllp', 'inc', 'pc', 'pllc']);
function formatEntityType(raw: string): string {
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) =>
      ENTITY_TYPE_ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ');
}

const CHECK_ICONS: Record<string, typeof FileText> = {
  'Bank Statements': FileSpreadsheet,
  'Tax Returns': FileText,
  'K-1 Forms': FileText,
  'P&L Statement': FileSpreadsheet,
  'Transaction Review': Building2,
  // Clarifications are optional, not adverse — a warning triangle cries wolf
  // (K3 #16). A clock reads "pending / outstanding" without implying severity.
  'Clarifications': Clock
};

export default function CaseReadinessPanel({
  caseId,
  compact = false,
  onStatusChange,
  showRefresh = true,
  className = ''
}: CaseReadinessPanelProps) {
  const [readiness, setReadiness] = useState<ReadinessResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!compact);

  const fetchReadiness = async () => {
    if (!caseId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await api.get<ReadinessResult>(`/api/v1/queue/case/${caseId}/readiness`);
      setReadiness(data);
      onStatusChange?.(data.is_ready);
    } catch (err: any) {
      console.error('Error fetching readiness:', err);
      setError(err.message || 'Failed to check readiness');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReadiness();
  }, [caseId]);

  if (loading) {
    return (
      <div className={`p-4 rounded-[2px] border ${className}`} style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="flex items-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm">Checking readiness...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 rounded-[2px] border ${className}`} style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
          <AlertTriangle className="w-4 h-4" style={{ color: 'var(--color-blocker)' }} />
          <span className="text-sm">{error}</span>
        </div>
        {showRefresh && (
          <button
            onClick={fetchReadiness}
            className="mt-2 text-xs hover:opacity-80 flex items-center gap-1"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <RefreshCw className="w-3 h-3" />
            Retry
          </button>
        )}
      </div>
    );
  }

  if (!readiness) return null;

  const overallStatus = readiness.is_ready ? 'passed' : 'failed';
  const OverallIcon = STATUS_CONFIG[overallStatus].icon;

  // Compact view for sidebar
  if (compact && !expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className={`w-full p-3 rounded-[2px] border text-left transition-colors hover:opacity-80 ${className}`}
        style={{
          borderColor: STATUS_CONFIG[overallStatus].border,
          backgroundColor: STATUS_CONFIG[overallStatus].bg,
          borderLeft: STATUS_CONFIG[overallStatus].accent
            ? `2px solid ${STATUS_CONFIG[overallStatus].accent}`
            : undefined
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <OverallIcon className="w-4 h-4" style={{ color: STATUS_CONFIG[overallStatus].color }} />
            <span className="text-sm font-medium" style={{ color: STATUS_CONFIG[overallStatus].color }}>
              {readiness.is_ready ? 'Ready for Review' : 'Not Ready'}
            </span>
          </div>
          <ChevronDown className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
        </div>
        {!readiness.is_ready && readiness.blocking_issues.length > 0 && (
          <p className="text-xs mt-1 truncate" style={{ color: 'var(--color-text-secondary)' }}>
            {readiness.blocking_issues[0]}
          </p>
        )}
      </button>
    );
  }

  return (
    <div className={`rounded-[2px] border overflow-hidden ${className}`} style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}>
      {/* Header — neutral band; a single 2px crimson left rule is the only hue
          marker for the adverse (not-ready) state (K3 review 2026-08-23). */}
      <div
        className="p-4 border-b"
        style={{
          borderColor: 'var(--color-border)',
          borderLeft: readiness.is_ready ? undefined : '2px solid var(--color-blocker)'
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-[2px] flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <OverallIcon className="w-5 h-5" style={{ color: STATUS_CONFIG[overallStatus].color }} />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: STATUS_CONFIG[overallStatus].color }}>
                {readiness.is_ready ? 'Ready for Accountant Review' : 'Not Ready for Review'}
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {readiness.overall_message}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {showRefresh && (
              <button
                onClick={fetchReadiness}
                disabled={loading}
                className="p-2 hover:opacity-80 rounded-[2px] transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            {compact && (
              <button
                onClick={() => setExpanded(false)}
                className="p-2 hover:opacity-80 rounded-[2px] transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Blocking Issues — adverse: crimson heading + markers, hueless body.
          Hue lives on the marker, not the sentence (K3 review 2026-08-23). */}
      {readiness.blocking_issues.length > 0 && (
        <div className="p-4 border-b" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: 'var(--color-blocker)' }}>
            <XCircle className="w-4 h-4" />
            Blocking Issues ({readiness.blocking_issues.length})
          </h4>
          <ul className="space-y-1">
            {readiness.blocking_issues.map((issue, i) => (
              <li key={i} className="text-sm flex items-start gap-2" style={{ color: 'var(--color-text-muted)' }}>
                <span className="mt-1" style={{ color: 'var(--color-blocker)' }}>•</span>
                {issue}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Checks List */}
      <div className="p-4">
        <h4 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--color-text-label)' }}>
          Document Checklist
        </h4>
        <div className="space-y-2">
          {readiness.checks.map((check, i) => {
            const config = STATUS_CONFIG[check.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.pending;
            const CheckIcon = CHECK_ICONS[check.name] || FileText;
            const StatusIcon = config.icon;

            return (
              <div
                key={i}
                className="flex items-start justify-between gap-3 p-3 rounded-[2px] border"
                style={{
                  borderColor: config.border,
                  backgroundColor: config.bg,
                  borderLeft: config.accent ? `2px solid ${config.accent}` : undefined
                }}
              >
                <div className="flex items-center gap-3 flex-shrink-0">
                  {/* File-type icon is structure → hueless (K3 #8). Only the
                      right-aligned status cluster carries hue. */}
                  <CheckIcon className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                  <div>
                    <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      {check.name}
                    </span>
                    {!check.required && (
                      <span className="ml-2 text-xs" style={{ color: 'var(--color-text-dim)' }}>(optional)</span>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-2 flex-1 min-w-0 justify-end">
                  <span className="text-xs leading-snug text-right" style={{ color: config.text }}>
                    {check.message}
                  </span>
                  <StatusIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: config.color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Entity Type */}
      {readiness.entity_type && readiness.entity_type !== 'unknown' && (
        <div className="px-4 pb-4">
          <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            Entity Type: <span className="cs-figure font-medium" style={{ color: 'var(--color-text-secondary)' }}>{formatEntityType(readiness.entity_type)}</span>
          </div>
        </div>
      )}

      {/* Upload — demoted to a ghost secondary (K3 #9): uploading documents
          resolves neither of the stated blockers (they clear in Reconcile), so a
          solid-orange primary here would be a lie in orange. Behavior unchanged. */}
      {!readiness.is_ready && (
        <div className="p-4 border-t" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
          <a
            href={`/wizard-v2?claim=${caseId}&step=3&section=upload`}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-[2px] border hover:opacity-90 transition-colors text-sm font-medium"
            style={{ backgroundColor: 'transparent', borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}
          >
            <Upload className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
            Upload Missing Documents
          </a>
        </div>
      )}
    </div>
  );
}
